// ---------------------------------------------------------------------------
// Shared time utilities — Phase 5C.1
//
// Single source of truth for ALL user-visible time formatting in Venuefy.
// Times are DISPLAY-ONLY conversions; stored values (24h "HH:MM" schedule
// strings, ISO timestamps) are never modified.
// ---------------------------------------------------------------------------

/**
 * Convert a 24-hour "HH:MM" string to 12-hour display format.
 *   "06:30" -> "6:30 AM"
 *   "14:45" -> "2:45 PM"
 *   "19:15" -> "7:15 PM"
 * Strings that already contain AM/PM (e.g. MC agenda labels) pass through.
 */
export function to12Hour(hhmm) {
  if (hhmm === null || hhmm === undefined) return "";
  const raw = String(hhmm).trim();
  if (!raw) return "";
  if (/[ap]\.?m\.?/i.test(raw)) return raw; // already 12-hour
  const m = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return raw;
  let h = parseInt(m[1], 10);
  if (h > 23) return raw;
  const suffix = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m[2]} ${suffix}`;
}

/** Parse "HH:MM" into minutes since midnight. Returns null if unparseable. */
export function timeToMinutes(hhmm) {
  const m = String(hhmm || "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/**
 * Current wall-clock minutes since midnight in the given IANA timezone
 * (the wedding/event timezone). Falls back to device time if the timezone
 * is missing or invalid, so guests traveling from other timezones still
 * see the correct "next" item.
 */
export function nowMinutesInZone(timeZone) {
  if (timeZone) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      }).formatToParts(new Date());
      const h = parseInt(parts.find((p) => p.type === "hour")?.value, 10);
      const min = parseInt(parts.find((p) => p.type === "minute")?.value, 10);
      if (!Number.isNaN(h) && !Number.isNaN(min)) return (h % 24) * 60 + min;
    } catch {
      // invalid timezone string — fall through to device time
    }
  }
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

/**
 * Index of the next upcoming schedule item based on event-local time.
 * Schedule is assumed sorted by time (backend sorts it).
 * If every event has passed, returns the FINAL event's index.
 * Returns -1 for an empty schedule.
 */
export function nextEventIndex(schedule, timeZone) {
  if (!Array.isArray(schedule) || schedule.length === 0) return -1;
  const now = nowMinutesInZone(timeZone);
  const idx = schedule.findIndex((s) => {
    const t = timeToMinutes(s.time);
    return t !== null && t >= now;
  });
  return idx === -1 ? schedule.length - 1 : idx;
}

/**
 * Format an ISO timestamp as a 12-hour time, e.g. "2:45 PM".
 * Locale is pinned to en-US so every device/browser shows 12-hour format.
 * Optional timeZone renders the time in the event's timezone.
 */
export function formatTimestamp(iso, timeZone) {
  if (!iso) return "";
  try {
    const opts = { hour: "numeric", minute: "2-digit" };
    if (timeZone) opts.timeZone = timeZone;
    return new Date(iso).toLocaleTimeString("en-US", opts);
  } catch {
    return "";
  }
}

/**
 * Format an ISO timestamp as date + 12-hour time, e.g. "Aug 21, 2:45 PM".
 */
export function formatDateTimestamp(iso, timeZone) {
  if (!iso) return "";
  try {
    const opts = { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };
    if (timeZone) opts.timeZone = timeZone;
    return new Date(iso).toLocaleString("en-US", opts);
  } catch {
    return "";
  }
}

/**
 * Live clock string in 12-hour format, e.g. "7:15 PM".
 * Optional timeZone shows the event-local clock (Projector).
 */
export function formatClock(timeZone) {
  return formatTimestamp(new Date().toISOString(), timeZone);
}
